import { useEffect, useState } from 'react';

import { ArrowRight, CheckCircle, ChevronRight, ClipboardList, MapPin, Shield, Users } from 'lucide-react';

export default function ViewEquipes() {
  const [state, setState] = useState<'idle' | 'building' | 'ready' | 'sent'>('idle');

  useEffect(() => {
    const t1 = setTimeout(() => setState('building'), 700);
    const t2 = setTimeout(() => setState('ready'), 2200);
    const t3 = setTimeout(() => setState('sent'), 3400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const ready = state === 'ready' || state === 'sent';

  return (
    <div className='grid md:grid-cols-2 gap-8 items-start demo-view-enter'>
      {/* Left — team builder */}
      <div className='space-y-5'>
        <div className='flex items-center gap-2'>
          {(['Équipe', 'Concours', 'Envoi'] as const).map((s, i) => (
            <div key={s} className='flex items-center gap-2'>
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                  i === 0
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 dark:shadow-blue-900/40'
                    : 'bg-slate-100 border border-slate-200 text-slate-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500'
                }`}
              >
                {i === 0 ? '1' : <span className='text-[10px]'>{i + 1}</span>}
              </div>
              <span
                className={`text-xs font-medium hidden sm:block ${
                  i === 0 ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-600'
                }`}
              >
                {s}
              </span>
              {i < 2 && <ChevronRight className='w-3 h-3 text-slate-300 dark:text-slate-700' />}
            </div>
          ))}
        </div>

        <h3 className='text-xl font-black text-slate-900 dark:text-white'>Composer une équipe club</h3>

        {/* Team card */}
        <div
          className={`border rounded-2xl p-4 transition-all duration-500 ${
            ready
              ? 'bg-emerald-50 border-emerald-200/60 dark:bg-slate-800/70 dark:border-emerald-500/30'
              : 'bg-slate-50 border-slate-200 dark:bg-slate-800/40 dark:border-slate-700'
          }`}
        >
          <p className='text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3'>Équipe</p>

          <div className='space-y-3'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-black text-white text-sm shrink-0'>
                MB
              </div>
              <div className='flex-1 min-w-0'>
                <p className='font-bold text-slate-900 dark:text-white text-sm'>Marc Bernard</p>
                <p className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>Capitaine · Licence OK</p>
              </div>
              <CheckCircle className='w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0' />
            </div>

            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center font-black text-white text-sm shrink-0'>
                SM
              </div>
              <div className='flex-1 min-w-0'>
                <p className='font-bold text-slate-900 dark:text-white text-sm'>
                  {state === 'idle' ? '—' : state === 'building' ? 'Sophie M…' : 'Sophie Martin'}
                </p>
                <p className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>
                  {ready ? 'Licence renseignée' : 'Ajout en cours…'}
                </p>
              </div>
              {ready ? (
                <CheckCircle className='w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0' />
              ) : (
                <span className='w-5 h-5 border-2 border-blue-500/60 border-t-transparent rounded-full animate-spin inline-block shrink-0' />
              )}
            </div>
          </div>
        </div>

        <div className='flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 dark:bg-slate-900/50 dark:border-slate-800 rounded-xl px-4 py-3'>
          <ClipboardList className='w-3.5 h-3.5 shrink-0 mt-0.5' />
          <span>Le club peut préparer des équipes “types” (Doublette / Triplette) et les inscrire en 1 clic.</span>
        </div>
      </div>

      {/* Right — contest selection + CTA */}
      <div className='space-y-4'>
        <div className='bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-800 rounded-2xl p-5'>
          <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4'>Concours ciblé</p>
          <p className='font-bold text-slate-900 dark:text-white mb-1'>Grand Prix Pétanque d'Été — Gerland</p>

          <div className='space-y-2 text-sm text-slate-500 dark:text-slate-400 mb-5'>
            <div className='flex items-center gap-2'>
              <MapPin className='w-3.5 h-3.5 shrink-0' />
              Lyon, 69 · 13 Juin 2026
            </div>
            <div className='flex items-center gap-2'>
              <Users className='w-3.5 h-3.5 shrink-0' />
              Doublette · {ready ? 'Bernard / Martin' : 'Bernard / …'}
            </div>
          </div>

          <div className='border-t border-slate-200 dark:border-slate-800 pt-4 flex items-baseline justify-between'>
            <span className='text-slate-500 dark:text-slate-400 text-sm'>Frais équipe</span>
            <span className='text-2xl font-black text-slate-900 dark:text-white'>30 €</span>
          </div>

          <p className='mt-2 text-[10px] text-slate-500 dark:text-slate-600'>
            Le club peut choisir : paiement par le club, ou par le capitaine.
          </p>
        </div>

        <button
          className={`w-full py-4 rounded-2xl font-black text-base flex items-center justify-center gap-3 transition-all duration-500 ${
            ready
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-blue-600/30 dark:hover:shadow-blue-900/40 hover:-translate-y-0.5'
              : 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-600'
          }`}
        >
          <ArrowRight className='w-5 h-5' />
          {state === 'sent' ? 'Inscription envoyée ✓' : ready ? "Envoyer l'inscription" : 'En attente de l’équipe…'}
        </button>

        <div className='flex items-center justify-center gap-5 text-xs text-slate-500 dark:text-slate-600'>
          <span className='flex items-center gap-1.5'>
            <Shield className='w-3 h-3' />
            Historique & traçabilité
          </span>
          <span className='flex items-center gap-1.5'>
            <CheckCircle className='w-3 h-3' />
            Zéro erreur de saisie
          </span>
        </div>
      </div>
    </div>
  );
}
